/**
 * The arcade view-model seam.
 *
 * WHY THIS FILE EXISTS. Until v0.15.0 every arcade theme rendered the same
 * markup, and `test/arcadeThemes.test.tsx` could prove hide-costs held for all
 * ten by asserting against that one tree. Structural layouts remove that
 * property: a raid board shows damage, a race shows a gap, a terminal prints a
 * table, and no single set of positional assertions covers them. So the
 * guarantee has to stop being positional and start being STRUCTURAL.
 *
 * That is what this module is. It is the only place in the arcade that sees a
 * raw `Player`. The type it produces — `BoardPlayer` — has NO `spend` field at
 * all, and carries the rank move only as a `Mover`: a DISCRIMINATED UNION
 * whose withheld variant has no delta on it to read. A layout renderer cannot
 * put a cost figure on screen because the number is not reachable from the
 * type it is handed, and cannot put a mover arrow on screen under hide-costs
 * because the variant it was given has no field to draw one from. Adding a
 * tenth skeleton cannot regress the governance, because a skeleton never
 * touches the payload.
 *
 * Three consequences worth keeping in mind when editing:
 *
 *  1. Every derivation that needs `spend` (the Whisperer archetype, the cost
 *     figure, the pot) happens HERE, while spend is still in scope, and only
 *     its safe OUTPUT crosses the seam. That is deliberate: it preserves the
 *     behaviour that exists when an admin has hide-costs off, without letting
 *     the number itself reach a renderer.
 *  2. `$▓▓▓` is produced in exactly one function (`figure`), not once per
 *     skeleton. The placeholder is the "there is a number here you cannot see"
 *     affordance, and it must never be reconstructible into a digit.
 *  3. Server-side suppression is unchanged and remains the real control:
 *     `backend/src/routes/leaderboard.ts` sends `spend: null` and `move: null`
 *     and the settings client fails closed. This module is defence in depth,
 *     not the defence.
 */

/** Matches `backend/src/routes/leaderboard.ts`'s `LeaderboardPlayer`. This is
 *  the WIRE shape — the only type in the arcade that still carries `spend` and
 *  `move`, and it must not escape this module into a renderer. */
export interface Player {
  /** Opaque, non-reversible per-player id (see backend `opaqueId`). Never an
   *  email. Keys the profile drill-down and its share link. */
  id: string
  name: string
  /** Null when the admin hide-costs setting is on. */
  spend: number | null
  tokens: number
  model: string | null
  models: number
  department: string
  /** Normalised office (Entra `city`), or `Unassigned`. Server-side
   *  normalisation only -- see backend `arcadeGroups.ts`. OPTIONAL on the wire
   *  so a payload from a server predating this field still parses; the seam
   *  defaults it, so `BoardPlayer.office` is always a string. */
  office?: string
  streak: number
  /** Daily token activity over the recent window — the row heartbeat. */
  spark: number[]
  /** Rank change, older half of the window vs newer half. Always null when
   *  hide-costs is on. Never crosses the seam in either case. */
  move: number | null
  /** Distinct topics touched this window (daily_topic_detail) — 0 means no
   *  diversity data, not necessarily zero topics. */
  topicsUsed: number
  /** Days since this player's first-ever recorded activity. Not spend-derived. */
  tenureDays: number
  /**
   * Distinct calendar days active this window.
   *
   * The honest adoption headline, and the board has never printed it: measured
   * 30 Aug 2026, twelve of twenty-two people have used the platform on exactly
   * ONE day, ever. Optional on the wire so a payload from a server predating
   * the field still parses.
   */
  activeDays?: number
  /** Requests this window and how many failed, from `LiteLLM_SpendLogs.status`
   *  (100% populated, 4.4% failure, read by nothing before 31 Aug 2026).
   *  `requests` of 0 means "no quality data", NOT "no failures". */
  requests?: number
  failed?: number
  /** Answer latency, this person's own p50/p90 in milliseconds. Null when no
   *  request carried a duration. Not spend-derived. */
  p50Ms?: number | null
  p90Ms?: number | null
  /** DEVELOPER KEY spend this window, shown BESIDE `spend`, never added to it
   *  and never ranked on. `spend` is chat (LibreChat's shared gateway key);
   *  this is their own virtual key. Null under hide-costs, like `spend`;
   *  0 means they simply hold no developer key. */
  keySpend?: number | null
  /**
   * WHAT THE TOTAL IS MADE OF: chat (a person typing into LibreChat) versus
   * Claude Code (their own developer key). `spend` and `tokens` above are
   * unchanged and still rank the row; `chat + code` equals each of them
   * exactly. Never sum a half alongside the total.
   *
   * NULL IS "NOT KNOWN", NEVER "NONE" — the reason `surfaceOf` refuses to
   * build a known split out of them and hands back `{ known: false }`. The
   * spend halves are additionally null under hide-costs, like `spend`; the
   * token halves are not spend-derived and survive it. Optional on the wire so
   * a payload from a server predating the split still parses.
   */
  chatSpend?: number | null
  codeSpend?: number | null
  chatTokens?: number | null
  codeTokens?: number | null
  you: boolean
}

export interface TopicCount {
  topic: string
  count: number
}

/** One day of one topic. Drives the taxonomy's small multiples, whose job is
 *  the SHAPE of a topic over time; the counts live in the table beneath. */
export interface TopicDayCount {
  day: string
  topic: string
  count: number
}

/** One agent's usage this window. `people` is a distinct COUNT; the payload
 *  carries no identifier for who they are. */
export interface AgentUsage {
  agent: string
  conversations: number
  people: number
}

export interface ArcadeUsage {
  agents: AgentUsage[]
  /** False means LibreChat Mongo could not be read, which is a different
   *  claim from "nobody used an agent" and is drawn as one. */
  available: boolean
  /** Conversations started in the window, and how many an AGENT drove.
   *  `daily_feature_usage.agents` reports 100% and is wrong (it tests
   *  `agent_id` for truthiness and `agent_id` is always set); this counts only
   *  `agent_<nanoid>`. */
  conversations: number
  agentConversations: number
  agentPeople: number
}

/* --------------------------------------------------------------------------
 * MCP TOOL USAGE
 *
 * From the nightly warehouse, which reads BOTH real sources: the ASGI emitter
 * (denials, latency, HTTP status, calling plane; live since 29 Aug 2026) and
 * Mongo `messages.content[].tool_call.name` (the whole history since 11 Aug,
 * with conversation linkage). Neither can answer the other's questions.
 *
 * THE GRAIN IS CARRIED IN THE TYPE NAMES ON PURPOSE. `McpTool.conversations`
 * is at TOOL grain and does not sum down a column; `McpServer.conversations`
 * is at SERVER grain and does. The views print that warning beside the column
 * rather than trusting anyone to remember it.
 * ------------------------------------------------------------------------ */

export interface McpServer {
  server: string
  calls: number
  people: number
  /** Distinct conversations AT SERVER GRAIN. Correct for this server; still
   *  does not sum across days or across servers. */
  conversations: number
  tools: number
}

/**
 * What a recorded tool call actually IS. Mirrors `ToolFamily` in
 * `backend/src/clients/mcpUsage.ts`, which is where the classification is made.
 *
 * THE PANEL USED TO DRAW ONE `(unknown)` BAR FOR ALL OF THIS. Measured live on
 * 31 Aug 2026 that bar was thirteen tools and three unrelated things, and its
 * largest member — `bash_tool`, 1,113 calls — is the most-used capability on
 * the platform and is not an MCP server at all. A bucket named after a parse
 * failure is a bucket nobody opens.
 */
export type ToolFamily = 'mcp' | 'code' | 'builtin' | 'handoff' | 'unknown'

/** Totals for one family. Every figure names its own grain. */
export interface McpFamilyTotals {
  family: ToolFamily
  calls: number
  people: number
  tools: number
  /** Conversations at TOOL grain, SUMMED — a ceiling, never a distinct count.
   *  A conversation using `bash_tool` and `read_file` is counted twice. */
  toolConversations: number
}

/** One day of one family's calls, over the SELECTED window. */
export interface McpFamilyDay {
  day: string
  family: ToolFamily
  calls: number
}

/** One subagent handoff target. `agent` is null when that agent has since been
 *  deleted; the handoffs still happened. */
export interface McpHandoff {
  agentId: string
  agent: string | null
  calls: number
  people: number
}

/** The pooled non-MCP reach bucket: the only honest CONVERSATION count for the
 *  built-ins, and it cannot be split by tool because the reach table stores no
 *  tool name. Drawn and labelled as a pool. */
export interface McpBuiltinReach {
  calls: number
  conversations: number
  people: number
}

export interface McpTool {
  tool: string
  server: string
  family: ToolFamily
  calls: number
  people: number
  /** Distinct conversations AT TOOL GRAIN. **Does not sum down the column** —
   *  a conversation that used four tools appears on four rows. */
  conversations: number
  /** The typical day's p50/p90 from the emitter, in ms. Null before 29 Aug
   *  2026, which is ordinary and is drawn as an absence, not as zero. */
  p50Ms: number | null
  p90Ms: number | null
  errors: number
  planes: string[]
}

/** One denial. A COUNT and no identifier: the decision table stores neither an
 *  email nor an id, so there is nothing here to expand into a person. */
export interface McpDenial {
  day: string
  server: string
  tool: string
  source: string
  calls: number
  people: number
}

export interface McpDecisionDay {
  day: string
  allowed: number
  denied: number
}

export interface McpPlane {
  source: string
  calls: number
}

/**
 * One (person, server, tool) cell of the activity heatmap.
 *
 * GRAIN: one row per person per server per tool, summed over the days in the
 * window. `calls` SUMS FREELY in every direction, which is the whole reason
 * the wire carries calls and not conversations — a conversation that used
 * four tools sits on four rows, so a conversation count at this grain adds up
 * to a number that means nothing. The heatmap pivots by summing `calls`, and
 * nothing else on this type may be summed into a cell.
 *
 * `id` is the SAME opaque id the board already keys profiles on, and `name`
 * the same derived display name. No email crosses the wire here for the same
 * reason none does anywhere else on this board.
 */
export interface McpPersonCell {
  id: string
  name: string
  server: string
  tool: string
  calls: number
}

export interface McpBoard {
  servers: McpServer[]
  tools: McpTool[]
  /** The heatmap's cells. Empty when the tool tables could not be read, which
   *  is `available: false` and is drawn as an absence, not as a zero. */
  people: McpPersonCell[]
  denials: McpDenial[]
  daily: McpDecisionDay[]
  planes: McpPlane[]
  families: McpFamilyTotals[]
  familyDaily: McpFamilyDay[]
  handoffs: McpHandoff[]
  builtinReach: McpBuiltinReach | null
  totalCalls: number
  totalPeople: number
  /** Conversation-SERVER pairs, not distinct conversations. Named for what it
   *  is everywhere it is drawn: the warehouse stores no cross-server
   *  conversation identity, so a thread that reached two servers is two. */
  totalConversations: number
  totalTools: number
  /**
   * False means "we could not read this", which is a DIFFERENT claim from
   * "nothing happened". The distinction exists because a missed GRANT on this
   * platform produces no error, no alarm and no log line, and a panel drawing
   * a confident zero over a table it never saw is the exact failure the
   * fail-closed trap describes.
   */
  available: boolean
}

/* --------------------------------------------------------------------------
 * THE KNOWLEDGE BASE (RAG)
 *
 * Two sources, and they answer different questions:
 *
 *  - WHAT IS IN THE CORPUS comes from `manual.chunks` in `chugpt_analytics`,
 *    the same database the panel already reads. Size and freshness only: the
 *    panel selects counts, titles, corpora and timestamps, and never
 *    `content`, `context` or `embedding`.
 *  - WHO SEARCHED IT comes from `daily_mcp_tool_usage`, because a knowledge
 *    base search IS an MCP tool call (`search_underwriting_manual`,
 *    `search_claims_manual`, `search_engineering_decisions`, all on
 *    `chu-data`). No new data path, no new instrumentation.
 *
 * WHAT IS NOT HERE, DELIBERATELY. There is no query text on this board and
 * there never will be. A search string is a staff member's own sentence, and
 * publishing "what people searched for" to everyone is the same exposure the
 * word cloud was declined for at this population size. The topic dimension is
 * the CORPUS and the DOCUMENT, both of which are fixed catalogue entries.
 * ------------------------------------------------------------------------ */

/** One corpus as the vector store holds it. `documents` counts distinct
 *  pages; `chunks` counts rows, and the two are not interchangeable — a page
 *  splits into as many chunks as its headings demand. */
export interface RagCorpus {
  corpus: string
  documents: number
  chunks: number
  /** Newest SOURCE page timestamp in this corpus, and the newest ingest.
   *  Different questions: "when did the manual last change" against "when did
   *  we last look". */
  lastUpdated: string | null
  lastSynced: string | null
  /** True where the corpus is built with model-written chunk context. Only
   *  `underwriting` is, and the difference is worth stating beside its size. */
  contextual: boolean
}

/** One day's searches against one corpus. */
export interface RagSearchDay {
  day: string
  corpus: string
  searches: number
}

/** One person's searches. Opaque id and derived name, exactly as the board
 *  identifies anybody else. */
export interface RagSearcher {
  id: string
  name: string
  searches: number
  /** How many distinct corpora they reached. */
  corpora: number
}

export interface RagBoard {
  corpora: RagCorpus[]
  /**
   * False means the vector store could not be read — most likely the
   * `manual` schema grant, which does not exist until the nightly bootstrap
   * adds it. A DIFFERENT claim from "the corpus is empty", and the panel
   * draws it as one, because a missing grant on this platform produces no
   * error and no alarm.
   */
  corpusAvailable: boolean
  daily: RagSearchDay[]
  searchers: RagSearcher[]
  byCorpus: Array<{ corpus: string; searches: number; people: number }>
  totalSearches: number
  totalPeople: number
  /** False means the warehouse tool tables could not be read. */
  available: boolean
}

const EMPTY_RAG: RagBoard = {
  corpora: [],
  corpusAvailable: false,
  daily: [],
  searchers: [],
  byCorpus: [],
  totalSearches: 0,
  totalPeople: 0,
  available: false,
}

/** Platform request quality. Group level only: `planes` names SERVICES
 *  (librechat-server, teams-bot, n8n-dev), never people. */
export interface QualityTotals {
  available: boolean
  requests: number
  failed: number
  p50Ms: number | null
  p90Ms: number | null
  cacheHits: number
  /** Successes — the only honest denominator for a cache hit rate, because a
   *  failed request never had a cache decision to hit or miss. */
  cacheable: number
  planes: McpPlane[]
}

/** One COMPLETED week of one rivalry pairing, as stored. */
export interface SeriesPeriod {
  periodStart: string
  /** The winning side, or null for a draw / a week a side did not field. */
  winner: string | null
  scoreA: number
  scoreB: number
}

/** One pairing's stored history, newest period first. Empty (or the whole
 *  array absent) whenever the series table has not been migrated or is
 *  unreachable -- the rivalry views fall back to a provisional series. */
export interface SeriesHistory {
  groupingKind: 'office' | 'team'
  sideA: string
  sideB: string
  periods: SeriesPeriod[]
}

/** The raw `GET /api/leaderboard?window=` response. One payload, every theme. */
export interface LeaderboardPayload {
  players: Player[]
  hideCosts: boolean
  /**
   * Whether the per-player CHAT / CLAUDE CODE breakdown means anything.
   *
   * False (or absent, which is a server predating the field) means the panel
   * could not read the developer-key list, so every row would have classified
   * as chat — "nobody here writes code", which is a confident wrong number
   * rather than a missing one. The board withholds the split and says so.
   * The ranked totals are unaffected either way.
   */
  surfaceSplitAvailable?: boolean
  topics?: TopicCount[]
  /** Per-day topic counts, for the small multiples. Empty is normal on a
   *  fresh deployment and the view says so. */
  topicDaily?: TopicDayCount[]
  defaultTheme?: string | null
  /** Agent usage. Absent/empty when LibreChat Mongo is unreachable. */
  usage?: ArcadeUsage
  /** MCP tool usage, from the three nightly warehouse tables. */
  mcp?: McpBoard
  /** Knowledge base corpora and who searched them. */
  rag?: RagBoard
  /** Platform request quality and the calling-plane split. */
  quality?: QualityTotals
  /** Stored series history for the shield. Absent/empty is normal. */
  series?: SeriesHistory[]
  /**
   * THE CONTEST FLOOR: how many active people a group must field to be played
   * as a side and to move the shield.
   *
   * It is NOT an exposure control, and that is the 31 Aug 2026 change. Every
   * office and every team is drawn at every value, with its `n` printed and a
   * hatched fill below the floor. Sent by the server so the fielding the board
   * renders and the fielding the close route enforces are one number.
   */
  contestFloor?: number
  /**
   * THE DAY AXIS FOR EVERY DAILY CHART, sent by the server.
   *
   * Oldest first, one entry per `spark` bucket, and bounded by the SELECTED
   * WINDOW. Before 31 Aug 2026 the server sent a fixed 21 buckets whatever the
   * window and the client reconstructed the dates from its own clock, so with
   * ROLLING 24H selected the race drew 08-11 to 08-31 — three weeks of axis
   * for a one-day window, twenty of whose days were structurally zero. Two
   * places computed "what days are these" and only one knew the window.
   *
   * Optional on the wire so a payload from a server predating the field still
   * parses; the seam falls back to reconstructing it, and says so.
   */
  sparkDays?: string[]
  /** True when the window covers MORE days than `sparkDays` carries — only
   *  `all`. The panels say so rather than letting the line be read as covering
   *  the same period as the totals beside it. */
  sparkTruncated?: boolean
}

export type Mode = 'spend' | 'tokens' | 'mains' | 'teams' | 'taxonomy' | 'rivalry' | 'usage' | 'rag' | 'buildlog'
/**
 * Named `ArcadeWindow` rather than `Window` so it cannot shadow the DOM type.
 *
 * The sub-day windows are ROLLING (1h/4h/8h/12h/24h back from now). `day` is
 * NOT one of them: it is a Sydney CALENDAR day, so `24h` and `day` genuinely
 * differ and both exist. The picker separates them into two groups for exactly
 * that reason -- presenting them as one scale is the misreading v0.12.1 had to
 * fix once already.
 */
export type ArcadeWindow = '1h' | '4h' | '8h' | '12h' | '24h' | 'day' | 'wk' | 'mo' | 'all'

/** The rolling sub-day windows, in order, for the picker's first group. */
export const ROLLING_WINDOWS: readonly ArcadeWindow[] = ['1h', '4h', '8h', '12h', '24h']
/** The calendar/period windows, in order, for the picker's second group. */
export const CALENDAR_WINDOWS: readonly ArcadeWindow[] = ['day', 'wk', 'mo', 'all']

/**
 * A pre-formatted number with a flag saying whether the real value was
 * withheld. Renderers print `text` and never format a currency value
 * themselves — which is the whole point: there is one currency formatter in
 * the arcade and it lives in this file.
 */
export interface Figure {
  readonly text: string
  readonly withheld: boolean
}

/** Costs render as pixelated blocks when withheld (hide-costs on). The ONLY
 *  place a `$` is produced anywhere in the arcade. */
export function figure(spend: number | null): Figure {
  return spend == null ? { text: '$▓▓▓', withheld: true } : { text: `$${spend.toFixed(2)}`, withheld: false }
}

/**
 * A rank move as a LAYOUT sees it.
 *
 * WHY A UNION AND NOT `move: number | null`. The governance property this
 * whole module exists to hold is that "hide-costs on" must be UNREPRESENTABLE
 * downstream, not merely unrendered. A nullable number does not give that:
 * `p.move ?? 0` compiles, and a skeleton written in a hurry would happily draw
 * `▲0` — or worse, an arrow keyed off `p.move !== undefined` — from a payload
 * that deliberately said nothing. The withheld variant here has NO delta field
 * at all, so `mover.places` is a compile error until the renderer has narrowed
 * on `shown`. That is the same trick `BoardPlayer` already plays with `spend`,
 * applied to the one value that had to come back.
 *
 * `text` is pre-formatted for the same reason `Figure.text` is: there is one
 * mover formatter in the arcade and it lives in this file, so seven skeletons
 * cannot disagree about what "moved up two" looks like.
 */
export type Mover =
  | { readonly shown: false }
  | { readonly shown: true; readonly dir: 'up' | 'down'; readonly places: number; readonly text: string }

/** The single withheld instance. Frozen and shared so a renderer cannot
 *  mutate a delta onto it, and so the common case allocates nothing. */
const NO_MOVER: Mover = Object.freeze({ shown: false as const })

/**
 * The ONLY place a `▲`/`▼` is produced anywhere in the arcade.
 *
 * `▲`/`▼` are reserved arcade-wide for rank moves and nothing else — the
 * ladder's promotion cut-line uses `◆` and drill-down links use `→`,
 * precisely because a bare `▲` on a rule is indistinguishable from "moved up
 * 2". A mover therefore ALWAYS carries its magnitude: the glyph never appears
 * on its own.
 *
 * Three inputs collapse to the withheld variant, and the first of them is the
 * governance one:
 *
 *   - `hideCosts` true. Belt and braces. The server already sends
 *     `move: null` in this case (`backend/src/routes/leaderboard.ts`) and its
 *     settings client fails closed, so this branch should be unreachable — but
 *     a mover must not become renderable on the strength of a server bug, and
 *     this is the cheapest place to say so.
 *   - `move` null. The server's "no prior-half activity" signal: genuinely
 *     new, so there is no earlier rank to have moved from. Deliberately NOT
 *     rendered as a "NEW" flag — tenure is already carried by `isNew`, and a
 *     word in the mover slot is exactly the stand-in the hide-costs invariant
 *     forbids.
 *   - `move` zero. Held position. The pre-v0.15 board printed `=` on every
 *     such row, which was most of the board; an arrow that only appears when
 *     something actually happened is the point of having one.
 */
export function mover(move: number | null, hideCosts: boolean): Mover {
  if (hideCosts || move == null || move === 0) return NO_MOVER
  return move > 0
    ? { shown: true, dir: 'up', places: move, text: `▲${move}` }
    : { shown: true, dir: 'down', places: -move, text: `▼${-move}` }
}

/**
 * WHAT ONE ROW'S TOTAL IS MADE OF — chat versus Claude Code.
 *
 * A UNION FOR THE SAME REASON `Mover` IS ONE. The failure this feature is one
 * slip away from is rendering `0` for a split nobody could compute: a reader
 * sees "0 code" and concludes the developer wrote none, which is a claim the
 * board never made. `known: false` carries NO numbers at all, so
 * `surface.codeTokensText` is a compile error until a renderer has narrowed —
 * the same trick that stops a skeleton drawing `▲0` from a withheld mover.
 *
 * Everything is PRE-FORMATTED, like `Figure.text` and `Mover.text`: there is
 * one token formatter and one currency formatter in the arcade and both live
 * in this file, so eight skeletons cannot disagree about what "1.0M" or
 * "$12.34" looks like. The costs are `Figure`s, so hide-costs blanks them to
 * `$▓▓▓` through the same path the score already uses.
 *
 * IT IS A BREAKDOWN AND NOT AN ADDITION. `chat + code` IS the row's total, so
 * no renderer may print a `+` in front of either half or add one to the score
 * beside it. `codeShare` exists so the split can be drawn as a proportion of
 * the row rather than as extra length hanging off it.
 */
export type Surface =
  | { readonly known: false }
  | {
      readonly known: true
      readonly chatTokensText: string
      readonly codeTokensText: string
      readonly chatCost: Figure
      readonly codeCost: Figure
      /** The Claude Code share of this row's TOKENS, 0..1. Tokens rather than
       *  dollars because dollars are withheld under hide-costs and a bar that
       *  vanished with the currency would be a second, silent behaviour. */
      readonly codeShare: number
      /** True when this person has any Claude Code activity at all this
       *  window. A row with none is a real answer — it is only the UNKNOWN
       *  case that must never render as a zero. */
      readonly hasCode: boolean
      /** True when this person has any chat activity at all this window. */
      readonly hasChat: boolean
    }

/** The single unknown instance. Frozen and shared for the same reason
 *  `NO_MOVER` is: a renderer cannot mutate figures onto it, and the case
 *  allocates nothing. */
const SPLIT_UNKNOWN: Surface = Object.freeze({ known: false as const })

/**
 * The ONLY place a chat/code split is built.
 *
 * Three things collapse to `{ known: false }`, and all three mean the same
 * thing — the board does not know:
 *
 *   - `available` false. The server could not read the developer-key roster,
 *     so its classification would have called every row on the platform chat.
 *   - either token half null. Belt and braces against a server that sets the
 *     flag but not the fields; a split must not become renderable on the
 *     strength of one of the two agreeing.
 *
 * Note what is NOT in that list: a person with zero Claude Code tokens. That
 * is a KNOWN split with an empty half, and it renders as one.
 */
export function surfaceOf(p: Player, available: boolean): Surface {
  if (!available) return SPLIT_UNKNOWN
  const chatTokens = p.chatTokens
  const codeTokens = p.codeTokens
  if (chatTokens == null || codeTokens == null) return SPLIT_UNKNOWN
  const total = chatTokens + codeTokens
  return {
    known: true,
    chatTokensText: fmtTokens(chatTokens),
    codeTokensText: fmtTokens(codeTokens),
    // `figure` already renders null as the withheld placeholder, which is
    // exactly what hide-costs sends for both halves.
    chatCost: figure(p.chatSpend ?? null),
    codeCost: figure(p.codeSpend ?? null),
    // Zero rather than NaN for a person with no tokens at all this window.
    codeShare: total > 0 ? codeTokens / total : 0,
    hasCode: codeTokens > 0,
    hasChat: chatTokens > 0,
  }
}

export function fmtTokens(t: number): string {
  if (t >= 1_000_000) return (t / 1_000_000).toFixed(1) + 'M'
  if (t >= 1_000) return (t / 1_000).toFixed(1) + 'K'
  return String(t)
}

export interface ClassInfo {
  name: string
  role: string
  /** A theme token reference, not a literal: the archetype accents have to
   *  move with the palette or a class reads as an off-brand smudge in nine of
   *  the ten themes. */
  color: string
}

type ClassSignals = Pick<Player, 'model' | 'tokens' | 'models' | 'streak' | 'spend' | 'topicsUsed'>

/** Class archetypes drawn from MULTIPLE signals — top model, token volume,
 *  arsenal breadth, streak length, spend-per-token efficiency, and topic
 *  diversity — instead of only the top model.
 *
 *  Deterministic and rarity-ordered: the rare, behaviour-driven archetypes are
 *  checked first so a genuine standout always wins; everyone else falls
 *  through to a model-only class.
 *
 *  Called from inside the seam, where `spend` is still in scope, so the
 *  Whisperer archetype keeps working for admins who have hide-costs OFF. Only
 *  the resulting `ClassInfo` crosses into a renderer. */
export function classFor(p: ClassSignals): ClassInfo {
  const m = (p.model ?? '').toLowerCase()

  // 10M tokens = the same bar as the "10M CLUB" badge — a huge-volume outlier.
  if (p.tokens >= 10_000_000) return { name: 'Titan', role: 'The Machine', color: 'var(--a-hot)' }
  // A full week+ of consecutive daily activity, well past the 3-day flame badge.
  if (p.streak >= 7) return { name: 'Streak', role: 'The Marathoner', color: 'var(--a-gold)' }
  // Same bar as the POLYGLOT badge: 4+ distinct models this window.
  if (p.models >= 4) return { name: 'Multi', role: 'The Polyglot', color: 'var(--a-violet)' }
  // 5+ distinct conversation topics this window (daily_topic_detail).
  if (p.topicsUsed >= 5) return { name: 'Curious', role: 'The Explorer', color: 'var(--a-cyan)' }
  // High tokens per dollar spent. Only evaluated when spend is visible.
  if (p.spend != null && p.spend > 0 && p.tokens / p.spend >= 2_000_000) {
    return { name: 'Efficient', role: 'The Whisperer', color: 'var(--a-accent)' }
  }

  if (m.includes('haiku')) return { name: 'Haiku', role: 'The Sprinter', color: 'var(--a-cyan)' }
  if (m.includes('opus')) return { name: 'Opus', role: 'The Oracle', color: 'var(--a-gold)' }
  if (m.includes('sonnet')) return { name: 'Sonnet', role: 'The Architect', color: 'var(--a-accent)' }
  if (m.includes('gpt')) return { name: 'GPT', role: 'The Outsider', color: 'var(--a-hot)' }
  if (m.includes('grok')) return { name: 'Grok', role: 'The Wildcard', color: 'var(--a-violet)' }
  return { name: p.model ?? '—', role: 'Operative', color: 'var(--a-accent)' }
}

export function levelOf(tokens: number): number {
  return Math.max(1, Math.min(99, Math.round(Math.cbrt(Math.max(tokens, 1)) / 4)))
}

export type Tag = { label: string; kind: '' | 'gold' | 'hot' }

/** The highest token-milestone tier a player has crossed (one badge, not five). */
export function tokenClub(tokens: number): Tag | null {
  if (tokens >= 100_000_000) return { label: '100M CLUB', kind: 'gold' }
  if (tokens >= 10_000_000) return { label: '10M CLUB', kind: 'gold' }
  if (tokens >= 1_000_000) return { label: '1M CLUB', kind: '' }
  if (tokens >= 100_000) return { label: '100K CLUB', kind: '' }
  return null
}

/** Tenure thresholds for the VETERAN/ROOKIE badge. Not spend-derived (built
 *  from first-ever activity date), so this shows regardless of hide-costs. */
const VETERAN_DAYS = 14
const ROOKIE_DAYS = 2
/** "New this window" for the org tiles and the split-flap NEW ARRIVAL status.
 *  Tenure, never a rank move. */
const NEW_DAYS = 7
/** The 🔥 badge threshold, reused for the "live streaks" org count so the two
 *  can never disagree about what a streak is. */
const STREAK_BADGE_DAYS = 3

/**
 * Up to four varied tags per row: a positional award, a token-milestone club,
 * a play-style trait, and a tenure badge.
 *
 * v0.15.0 removed two labels here, both deliberately:
 *
 *  - WOODEN SPOON. The board is named, company-wide and linked to all staff,
 *    and the design set's own constraint 03 forbids calling out the bottom.
 *    Season Ladder's Development League is the replacement framing.
 *  - BIG SPENDER. It was dead code — the branch tested `(p.spend ?? 0) >= 100`
 *    and `spend` is null under hide-costs — but it was exactly the kind of
 *    client-side cost path the test suite exists to prevent, and leaving it
 *    dormant behind nine new renderers was not worth it.
 */
export function tagsFor(p: Pick<Player, 'tokens' | 'models' | 'tenureDays'>, index: number, total: number): Tag[] {
  const out: Tag[] = []
  if (index === 0) out.push({ label: 'CHAMPION', kind: 'gold' })
  else if (index === 1) out.push({ label: 'RUNNER-UP', kind: '' })
  else if (index === 2) out.push({ label: 'BRONZE', kind: '' })
  else if (total > 6 && index < Math.ceil(total * 0.15)) out.push({ label: 'ELITE', kind: '' })

  const club = tokenClub(p.tokens)
  if (club) out.push(club)

  if (p.models >= 4) out.push({ label: 'POLYGLOT', kind: '' })
  else if (p.models === 1) out.push({ label: 'SPECIALIST', kind: '' })

  if (p.tenureDays >= VETERAN_DAYS) out.push({ label: 'VETERAN', kind: '' })
  else if (p.tenureDays <= ROOKIE_DAYS) out.push({ label: 'ROOKIE', kind: '' })

  return out.slice(0, 4)
}

/**
 * One player as a LAYOUT sees them.
 *
 * Note what is absent: no `spend`, and no raw `move`. That absence is the
 * governance guarantee expressed as a type. Everything a skeleton could want
 * from those two fields is already here in derived, safe form — `score` and
 * `cost` are pre-formatted `Figure`s, `mover` is a `Mover` whose withheld
 * variant carries no delta, and the remaining deltas are `streak` and
 * `tenureDays`, neither of which is spend-derived.
 */
export interface BoardPlayer {
  id: string
  name: string
  /** First token of the derived display name — what the podium and award
   *  cards show. Still a derived name, never an address. */
  first: string
  department: string
  /** Normalised office. Carried so the rivalry views can group without a
   *  second payload, and safe to carry: it is a place name, never an address
   *  and never an identifier. */
  office: string
  tokens: number
  /** `fmtTokens(tokens)` — precomputed so a skeleton never re-formats. */
  tokensText: string
  model: string | null
  models: number
  streak: number
  spark: number[]
  topicsUsed: number
  tenureDays: number
  /** Distinct days active this window. Safe across the seam: it is a count of
   *  days, not a cost, and it is the number that separates "tried it once"
   *  from "uses it". */
  activeDays: number
  /** Requests, failures and latency. All four are safe across the seam for the
   *  same reason `streak` is: none of them is spend-derived, so hide-costs has
   *  nothing to suppress. `requests === 0` means NO DATA, and `failRate` is
   *  null in that case rather than a confident 0%. */
  requests: number
  failed: number
  failRate: number | null
  p50Ms: number | null
  p90Ms: number | null
  /** DEVELOPER KEY spend this window, carried BESIDE the score and never part
   *  of it. The board ranks chat spend; this sits next to it so the second
   *  path is visible without changing what the ranking means. Null under
   *  hide-costs; 0 means no developer key.
   *
   *  THE SAME DOLLARS AS `surface.codeCost`, seen from the key side rather
   *  than the surface side. Never render both in one place: two figures that
   *  are one amount read as two amounts. */
  keySpend: number | null
  /** What this row's total is MADE OF — chat versus Claude Code. `{ known:
   *  false }` when the board could not compute it, which carries no numbers
   *  so a renderer cannot print a zero for it. See `surfaceOf`. */
  surface: Surface
  you: boolean
  /** 1-based position in `ranked`. */
  rank: number
  level: number
  cls: ClassInfo
  badges: Tag[]
  /** The active mode's score, already formatted. Tokens in every mode when
   *  hide-costs is on, because that is the only score the payload carries. */
  score: Figure
  /** The cost figure, or the withheld placeholder. Rendered only by skeletons
   *  that have a cost slot; `withheld` is true whenever hide-costs is on. */
  cost: Figure
  /** Tenure-derived, NOT a rank move. Drives the split-flap NEW ARRIVAL flag
   *  and the "new this window" counts. */
  isNew: boolean
  /** The rank move over this window, as the only shape a renderer may have it
   *  in. `{ shown: false }` whenever hide-costs is on, whenever the player has
   *  no earlier rank, and whenever they held position — and that variant has
   *  no delta on it to render. See `mover()`. */
  mover: Mover
}

export interface TeamStanding {
  dept: string
  /** Tokens when hide-costs is on, spend otherwise — already a number for bar
   *  widths, and already formatted in `text` for display. */
  amt: number
  text: string
  mvp: BoardPlayer | null
}

/** Org-wide totals every skeleton's summary tiles read from. All counted over
 *  the same `players[]` the board already has — no extra field, no extra call. */
export interface OrgTotals {
  players: number
  totalTokens: number
  totalTokensText: string
  /** Players on a streak of 3+ consecutive days — the same bar as the 🔥
   *  badge. NOT `>= 1`: nearly everyone with activity in the window has a
   *  1-day streak, so that threshold made the tile read exactly the same as
   *  the active-staff count and told a viewer nothing. */
  liveStreaks: number
  /** First-ever activity within NEW_DAYS. Tenure, not a mover. */
  newThisWindow: number
  /** The next round token milestone above `totalTokens` — the Raid Boss HP
   *  target. Derived client-side precisely so no goal field has to be added to
   *  the shared payload. */
  nextMilestone: number
  nextMilestoneText: string
  /**
   * Platform spend this window divided by the number of active people.
   *
   * A `Figure`, NOT a number, and that is the whole point. Cost per person is
   * a spend figure like any other, so it obeys hide-costs the same way the pot
   * does: it is derived HERE, inside the seam, where `spend` is still in
   * scope, and only its formatted output crosses into a renderer. There is no
   * numeric field on this type for a skeleton to print, divide, or reconstruct
   * a total from -- the guarantee is the absence of the number, not a runtime
   * check that a hurried renderer could forget to write.
   *
   * Withheld (`$▓▓▓`) whenever hide-costs is on, and also when there are no
   * active people at all, because a division by zero is not a cost.
   */
  costPerPerson: Figure
}

export interface BoardModel {
  mode: Mode
  window: ArcadeWindow
  hideCosts: boolean
  ranked: BoardPlayer[]
  podium: BoardPlayer[]
  leader: BoardPlayer | null
  /** The strongest player outside the podium — the Rising Star award. */
  risingStar: BoardPlayer | null
  you: { player: BoardPlayer; chasing: BoardPlayer | null } | null
  teams: TeamStanding[]
  teamMax: number
  topics: TopicCount[]
  topicDaily: TopicDayCount[]
  org: OrgTotals
  /** The credits "POT" readout. Withheld under hide-costs. */
  pot: Figure
  /** Agent usage, straight through from the payload. Empty when LibreChat
   *  Mongo is unreachable -- the panel renders its empty state. */
  usage: ArcadeUsage
  /** MCP tool usage, from the nightly warehouse. `available: false` means the
   *  tables could not be read, which the view says out loud. */
  mcp: McpBoard
  /** Knowledge base corpora and searches. Both halves degrade on their own:
   *  `available` is the usage half, `corpusAvailable` the vector store. */
  rag: RagBoard
  /** Platform request quality and the calling-plane split. */
  quality: QualityTotals
  /** Stored series history, for the perpetual shield. Empty is normal and
   *  means the rivalry views show a PROVISIONAL series instead. */
  series: SeriesHistory[]
  /** How many active people a group must field to be played as a SIDE. Not an
   *  exposure control: every group is drawn at every value. */
  contestFloor: number
  /**
   * The Sydney calendar days every daily chart on this board is drawn over,
   * oldest first. Length equals every player's `spark.length`.
   *
   * THE WINDOW PILLS DRIVE THIS, and therefore drive every chart that reads
   * it. One day means the window is sub-day and a day-by-day series is not a
   * thing that exists: the panels say so rather than widening the range or
   * drawing a one-point "trend".
   */
  days: string[]
  /** True when the window covers more days than `days` carries (`all` only). */
  daysTruncated: boolean
  /**
   * Whether the CHAT / CLAUDE CODE split is known this render.
   *
   * Every `BoardPlayer.surface` is `{ known: false }` when this is false, so a
   * row-level renderer needs nothing else. It is carried at board level
   * because the NOTE is a board-level statement: it says the board could not
   * read the key roster, not that any particular person did no coding.
   */
  surfaceSplit: boolean
}

/**
 * Can this window support a day-by-day series at all?
 *
 * Two points is the floor: one point is a number, not a trend, and a chart
 * drawn through it says something the data does not. Every daily panel gates
 * on this and prints an honest sentence instead when it is false — which is
 * the whole point of the 31 Aug 2026 fix. Silently widening the range to make
 * a chart appear is how the board came to contradict its own controls.
 */
export function supportsDailySeries(model: Pick<BoardModel, 'days'>): boolean {
  return model.days.length >= 2
}

/** How many whole weeks of heartbeat a window carries. The bump chart needs
 *  TWO to say anything — a bump with one column is a scatter of dots — so it
 *  is gated on this rather than on `days.length` alone. */
export function weeksAvailable(model: Pick<BoardModel, 'days'>): number {
  return Math.floor(model.days.length / WEEK_DAYS)
}

/**
 * The client-side default CONTEST floor.
 *
 * Three, not five, and it does ONE job: it decides whether a group can be
 * played as a side and move the shield. It does not decide whether a group is
 * SHOWN — every office and every team is drawn at every value, with its active
 * count printed and a hatched fill below the floor. The old five did both, and
 * the exposure half withheld nothing: a one-person office's per-person score
 * was pooled out of sight two tabs away from a board printing that same
 * person's name, department, office, spend, tokens and streak.
 *
 * Mirrors `DEFAULT_CONTEST_FLOOR` in `backend/src/clients/arcadeSeries.ts`.
 * Only used when the payload carries no floor at all.
 */
export const DEFAULT_CONTEST_FLOOR = 3

/** The empty MCP board. `available: false` -- "we could not read this", which
 *  is what a board with no payload half honestly is. */
const EMPTY_MCP: McpBoard = {
  servers: [],
  tools: [],
  people: [],
  denials: [],
  daily: [],
  planes: [],
  families: [],
  familyDaily: [],
  handoffs: [],
  builtinReach: null,
  totalCalls: 0,
  totalPeople: 0,
  totalConversations: 0,
  totalTools: 0,
  available: false,
}

/** The empty agent board. `available: false` -- "LibreChat could not be read",
 *  which is not the same claim as "nobody used an agent". */
const EMPTY_USAGE: ArcadeUsage = {
  agents: [],
  available: false,
  conversations: 0,
  agentConversations: 0,
  agentPeople: 0,
}

const EMPTY_QUALITY: QualityTotals = {
  available: false,
  requests: 0,
  failed: 0,
  p50Ms: null,
  p90Ms: null,
  cacheHits: 0,
  cacheable: 0,
  planes: [],
}

/** Round milestones for the shared Raid Boss goal, in tokens. */
const MILESTONES = [50e6, 100e6, 150e6, 250e6, 500e6, 1e9, 2.5e9, 5e9]

function nextMilestoneAbove(total: number): number {
  for (const m of MILESTONES) if (m > total) return m
  // Past the table: round up to the next whole 5B so the bar never pins full.
  return Math.ceil((total + 1) / 5e9) * 5e9
}

/**
 * The seam. Converts one `/api/leaderboard` payload into the model every
 * skeleton renders from.
 *
 * `mode` and `window` are inputs rather than payload fields because they are
 * view state — the same payload produces a different model as the viewer
 * switches mode, with no refetch.
 */
export function toViewModel(payload: LeaderboardPayload, mode: Mode, window: ArcadeWindow): BoardModel {
  const hideCosts = payload.hideCosts
  const players = payload.players ?? []
  // Absent means a server predating the split, which is the same state of
  // knowledge as a gateway that did not answer: unknown, so say so.
  const surfaceSplit = payload.surfaceSplitAvailable === true

  // Ranking. Spend can only order the board when the payload carries spend;
  // every other mode ranks on tokens. Before v0.15.0 `mains` fell through to
  // the spend comparator, so under hide-costs every player scored 0 and the
  // mode was effectively unsorted — that is the bug this line fixes.
  const byTokens = (a: Player, b: Player) => b.tokens - a.tokens
  const bySpend = (a: Player, b: Player) => (b.spend ?? 0) - (a.spend ?? 0)
  const sorted = [...players].sort(mode === 'spend' && !hideCosts ? bySpend : byTokens)

  const total = sorted.length
  const ranked: BoardPlayer[] = sorted.map((p, i) => ({
    id: p.id,
    name: p.name,
    first: p.name.split(' ')[0] ?? p.name,
    department: p.department,
    office: p.office ?? 'Unassigned',
    tokens: p.tokens,
    tokensText: fmtTokens(p.tokens),
    model: p.model,
    models: p.models,
    streak: p.streak,
    spark: p.spark,
    topicsUsed: p.topicsUsed,
    tenureDays: p.tenureDays,
    keySpend: p.keySpend ?? null,
    surface: surfaceOf(p, surfaceSplit),
    you: p.you,
    rank: i + 1,
    level: levelOf(p.tokens),
    cls: classFor(p),
    badges: tagsFor(p, i, total),
    // Tokens are the score in every mode the payload can actually rank when
    // costs are hidden; `spend` mode is removed from the controls in that case.
    score: mode === 'spend' && !hideCosts ? figure(p.spend) : { text: fmtTokens(p.tokens), withheld: false },
    cost: figure(p.spend),
    activeDays: p.activeDays ?? 0,
    requests: p.requests ?? 0,
    failed: p.failed ?? 0,
    // Null rather than 0 when there were no requests: "no failures" and "no
    // data" are different claims, and printing 0% for the second is exactly
    // the kind of confident wrong number this seam exists to stop.
    failRate: (p.requests ?? 0) > 0 ? (p.failed ?? 0) / (p.requests ?? 1) : null,
    p50Ms: p.p50Ms ?? null,
    p90Ms: p.p90Ms ?? null,
    isNew: p.tenureDays <= NEW_DAYS,
    // The ONE place `p.move` is read. `hideCosts` is passed in rather than
    // relied upon through `p.move` already being null, so the client's
    // suppression does not depend on the server having got it right.
    mover: mover(p.move, hideCosts),
  }))

  const byId = new Map(ranked.map((p) => [p.id, p]))

  // Team standings. Totals by spend, or by tokens when costs are hidden.
  const amounts = new Map<string, number>()
  // Tracked as { player, amount } rather than comparing against the stored
  // BoardPlayer: a BoardPlayer has no `spend`, so when costs are visible there
  // would be nothing on it to compare the running best against.
  const mvps = new Map<string, { player: BoardPlayer; amount: number }>()
  for (const p of players) {
    const v = hideCosts ? p.tokens : p.spend ?? 0
    amounts.set(p.department, (amounts.get(p.department) ?? 0) + v)
    const bp = byId.get(p.id)
    if (!bp) continue
    const cur = mvps.get(p.department)
    if (!cur || v > cur.amount) mvps.set(p.department, { player: bp, amount: v })
  }
  const teams: TeamStanding[] = [...amounts.entries()]
    .map(([dept, amt]) => ({
      dept,
      amt,
      text: hideCosts ? fmtTokens(amt) : `$${amt.toFixed(2)}`,
      mvp: mvps.get(dept)?.player ?? null,
    }))
    .sort((a, b) => b.amt - a.amt)

  const totalTokens = players.reduce((s, p) => s + p.tokens, 0)
  const totalSpend = players.reduce((s, p) => s + (p.spend ?? 0), 0)
  const milestone = nextMilestoneAbove(totalTokens)

  // THE DAY AXIS. Straight from the payload, which is the only place that
  // knows the window. The fallback exists solely so a payload from a server
  // predating the field still renders: it reconstructs the last N days from
  // the client clock, which is exactly the guess that produced the 24H bug, so
  // it is a compatibility path and never the normal one.
  const sparkSpan = Math.max(...players.map((p) => p.spark.length), 0)
  const days =
    payload.sparkDays ??
    Array.from({ length: sparkSpan }, (_, i) =>
      new Date(Date.now() - (sparkSpan - 1 - i) * 86_400_000).toISOString().slice(0, 10),
    )

  const youIdx = ranked.findIndex((p) => p.you)
  const leader = ranked[0] ?? null
  // Rising star: the strongest player OUTSIDE the podium; falls back to #2 on
  // a tiny board.
  const pool = ranked.slice(3)
  const risingStar = pool.length > 0 ? pool.reduce((a, b) => (b.tokens > a.tokens ? b : a)) : ranked[1] ?? null

  return {
    mode,
    window,
    hideCosts,
    ranked,
    podium: ranked.slice(0, 3),
    leader,
    risingStar,
    you:
      youIdx < 0
        ? null
        : { player: ranked[youIdx]!, chasing: ranked[youIdx - 1] ?? null },
    teams,
    teamMax: teams[0]?.amt ?? 0,
    topics: payload.topics ?? [],
    topicDaily: payload.topicDaily ?? [],
    org: {
      players: players.length,
      totalTokens,
      totalTokensText: fmtTokens(totalTokens),
      liveStreaks: players.filter((p) => p.streak >= STREAK_BADGE_DAYS).length,
      newThisWindow: players.filter((p) => p.tenureDays <= NEW_DAYS).length,
      nextMilestone: milestone,
      nextMilestoneText: fmtTokens(milestone),
      // Through `figure` like every other cost. `hideCosts` is tested here
      // rather than relied upon through `totalSpend` already summing to zero
      // from nulls: a zero total would otherwise render as a confident
      // "$0.00 per person" on a board that was told to say nothing.
      costPerPerson:
        hideCosts || players.length === 0 ? figure(null) : figure(totalSpend / players.length),
    },
    // The pot is a cost figure like any other, so it goes through `figure`.
    pot: hideCosts ? figure(null) : figure(totalSpend),
    usage: payload.usage ?? EMPTY_USAGE,
    // Normalised rather than passed straight through: `people` landed on
    // 31 Aug 2026 and a payload from a server predating it carries no such
    // key, which would hand a renderer `undefined` where the type promises an
    // array. Same compatibility rule as `Player.office`.
    mcp: payload.mcp ? { ...payload.mcp, people: payload.mcp.people ?? [] } : EMPTY_MCP,
    rag: payload.rag ?? EMPTY_RAG,
    quality: payload.quality ?? EMPTY_QUALITY,
    series: payload.series ?? [],
    // Defaulted rather than assumed: an older server that does not send this
    // yet gets the same floor the server enforces.
    contestFloor: payload.contestFloor ?? DEFAULT_CONTEST_FLOOR,
    days,
    daysTruncated: payload.sparkTruncated ?? false,
    surfaceSplit,
  }
}

/* ==========================================================================
 * TRAJECTORY: a sentence about a SERIES, never about a person
 *
 * READ THIS BEFORE ADDING A PHRASE.
 *
 * This board is linked to every member of staff and prints colleagues by name
 * with a rank beside them. A generated remark about a named person's
 * "outlook", sitting in that row, is not a leaderboard of token counts any
 * more: it is a machine's assessment of a colleague, published internally,
 * with no right of reply. The design set already forbids calling out the
 * bottom (`tagsFor` dropped WOODEN SPOON for exactly this reason) and the
 * rivalry footer already says volume is not productivity. A phrase generator
 * that undid either would be the single most damaging thing on the page.
 *
 * So THREE constraints are enforced by construction, not by review:
 *
 *  1. THE INPUT TYPE CANNOT SEE RANK, TOKENS OR SPEND. `OutlookSignals` is a
 *     `Pick` of four fields — the daily heartbeat, days active, tenure and
 *     streak — and nothing else is reachable. A comparative sentence is not
 *     merely discouraged, it is unwriteable: there is no other player in
 *     scope and no position to compare against.
 *  2. EVERY PHRASE IS IN ONE FROZEN TABLE BELOW, in full, as a literal. None
 *     is assembled from fragments, so the complete set of things this board
 *     can say about a colleague is readable in one screen and reviewable by
 *     someone who does not read TypeScript.
 *  3. EVERY PHRASE DESCRIBES THE SERIES. "More use in the second half of the
 *     window" is a fact about a line. "Falling behind" is a verdict about a
 *     person, and it is the sentence this whole apparatus exists to make
 *     impossible.
 *
 * Rejected, and each for the same reason: "falling behind", "catching up",
 * "ahead of N colleagues" (comparative); "light user", "power user", "highly
 * engaged" (labels the person, not the week); "at risk of dropping off",
 * "outlook: declining" (a forecast about a colleague); "should be using this
 * more" (advice about someone to an audience that is not them); "+42% week on
 * week" (a precise growth figure computed off two or three active days is
 * noise wearing a number's clothes).
 * ======================================================================== */

/** The ONLY fields a trajectory phrase may be derived from. Deliberately
 *  narrow: see the note above. */
export type OutlookSignals = Pick<BoardPlayer, 'spark' | 'activeDays' | 'tenureDays' | 'streak'>

export type OutlookCode =
  | 'window-too-short'
  | 'first-week'
  | 'single-day'
  | 'second-day'
  | 'streak'
  | 'rising'
  | 'steady'
  | 'easing'
  | 'quiet-recently'
  | 'in-bursts'

/**
 * THE COMPLETE PHRASE LIST. Nothing else is ever printed in this column.
 *
 * `{n}` is the only substitution, and it is a count of DAYS. There is no
 * substitution slot for a name, a rank, a team or a figure, so no phrase can
 * grow one without an edit that shows up in review.
 */
export const OUTLOOK_PHRASES: Readonly<Record<OutlookCode, string>> = Object.freeze({
  'window-too-short': 'Too short a window to read a trajectory',
  'first-week': 'First week — building momentum',
  'single-day': 'Active on one day in this window',
  'second-day': 'Came back for a second day',
  streak: 'On a run of {n} consecutive days',
  rising: 'More use in the second half of the window',
  steady: 'Steady across the window',
  easing: 'Less use in the second half than the first',
  'quiet-recently': 'Nothing recorded in the last {n} days of the window',
  'in-bursts': 'Used in bursts rather than daily',
})

export interface Outlook {
  readonly code: OutlookCode
  readonly text: string
}

/** How far apart the two halves must be before the phrase says the shape
 *  changed. A quarter, because on two or three active days anything tighter
 *  is describing one afternoon. */
const OUTLOOK_SHIFT = 0.25

/**
 * The trajectory phrase for one player's heartbeat.
 *
 * Ordered from the most specific and most certain to the most general, so a
 * genuinely new person is never described with a shape read off two days, and
 * the generic "steady" is the fall-through rather than the headline.
 */
export function outlookFor(p: OutlookSignals): Outlook {
  const say = (code: OutlookCode, n?: number): Outlook => ({
    code,
    text: n === undefined ? OUTLOOK_PHRASES[code] : OUTLOOK_PHRASES[code].replace('{n}', String(n)),
  })

  const spark = p.spark
  // Two buckets is the same floor every other daily form on this board uses:
  // one point is a number, not a trend, and a sentence about its direction
  // would be inventing one.
  if (spark.length < 2) return say('window-too-short')

  // Tenure first. Somebody in their first week has a shape, but describing it
  // as rising or easing reads a trend into three days of finding the buttons.
  if (p.tenureDays <= NEW_DAYS) return say('first-week')
  if (p.activeDays <= 1) return say('single-day')
  if (p.activeDays === 2) return say('second-day')
  if (p.streak >= STREAK_BADGE_DAYS) return say('streak', p.streak)

  // Silence at the end of the window is the one shape worth naming on its own:
  // "less than the first half" is a poor description of a series that simply
  // stops. Counted in whole days so the sentence and the sparkline agree.
  let trailingQuiet = 0
  for (let i = spark.length - 1; i >= 0 && (spark[i] ?? 0) === 0; i--) trailingQuiet += 1
  if (trailingQuiet >= Math.max(2, Math.floor(spark.length / 3))) {
    return say('quiet-recently', trailingQuiet)
  }

  const mid = Math.floor(spark.length / 2)
  const sum = (from: number, to: number) => spark.slice(from, to).reduce((n, v) => n + (v || 0), 0)
  const first = sum(0, mid)
  const second = sum(mid, spark.length)
  if (first > 0) {
    if (second > first * (1 + OUTLOOK_SHIFT)) return say('rising')
    if (second < first * (1 - OUTLOOK_SHIFT)) return say('easing')
  } else if (second > 0) {
    return say('rising')
  }

  // Gappy but present: more days off than on, with three or more days used.
  const usedDays = spark.filter((v) => (v || 0) > 0).length
  if (usedDays >= 3 && usedDays * 2 < spark.length) return say('in-bursts')
  return say('steady')
}

/* ==========================================================================
 * RIVALRY: Office of Origin and Team Wars
 * ========================================================================== */

/**
 * WHY THE SCORE IS TOKENS PER ACTIVE PERSON, AND NEVER SPEND.
 *
 * Three separate constraints all land on the same answer.
 *
 *  1. PER PERSON, so headcount cannot decide it. Sydney has sixteen active
 *     people and Brisbane four; a totals-based contest is a headcount chart
 *     with a scoreline printed on it, and Brisbane could never win it however
 *     hard anyone worked.
 *  2. PER PERSON, so no INDIVIDUAL can decide it either. On a four-person
 *     side a single heavy user moves a total enormously — and the moment a
 *     side's result is visibly one person's output, that person is
 *     identifiable as the reason their side won or lost. That is the outcome
 *     this whole design exists to prevent, and it is why `MIN_ACTIVE` exists
 *     alongside the normalisation.
 *  3. TOKENS, never spend, so the scoreline means the same thing whether or
 *     not hide-costs is on. A rivalry that silently switched measure with an
 *     admin setting would make two weeks incomparable, and it would put a cost
 *     figure into a view that has no business carrying one. These views
 *     therefore touch no `Figure` at all.
 */
export interface RivalrySide {
  name: string
  /** Tokens per active person. */
  score: number
  scoreText: string
  /** How many people this side fielded. Shown, because a scoreline without it
   *  invites the reader to infer a headcount that is not there. */
  active: number
}

/**
 * A group in the standings.
 *
 * `fieldable` REPLACED `eligible` on 31 Aug 2026, and the rename is the whole
 * change. `eligible` decided whether a group was DRAWN; `fieldable` decides
 * only whether it can be played as a side and move the shield. Every group is
 * now in `sides`, always, with its `active` count printed and `thin` set below
 * the floor so the view can hatch it instead of hiding it.
 *
 * The justification is consistency, and it is airtight: no aggregate on this
 * board can be more revealing than the individual rows already published to
 * the same audience on the same page. Melbourne's "hidden" per-person score
 * was one person whose name, department, office, dollar spend, token count,
 * streak, rank and shareable card sat two tabs away. Pooling withheld a
 * comparison, not an identity.
 */
export interface RivalryStanding extends RivalrySide {
  /** Big enough to be played as a side and to move the shield. */
  fieldable: boolean
  /** Below the contest floor: draw it hatched, print the `n`, and say the
   *  number is one or two people's week. Secondary encoding, never
   *  suppression. */
  thin: boolean
  /** Which selector this group came from. Carried so a mixed league can say
   *  "Sydney (office) v IT (team)" rather than implying one namespace. */
  dimension: 'office' | 'team'
  /** Everyone in the group, active or not. `active` is the scoring
   *  denominator; this is the group's size, and the two differ by exactly the
   *  people who did nothing this window. */
  members: number
}

/** One completed period's outcome from the drawing side's point of view.
 *  `null` is a draw, or a period neither side properly fielded. */
export type Pip = 'a' | 'b' | null

export interface RivalryFixture {
  a: RivalrySide
  b: RivalrySide
  /** At most three periods, OLDEST FIRST so the strip reads left to right. */
  pips: Pip[]
  /**
   * False when the pips were recomputed from the sparkline rather than read
   * from the series table. The UI says so out loud: a provisional series is
   * an estimate of recent form, not a record, and labelling it as a record
   * would be the same category error as a stored result that never happened.
   */
  stored: boolean
  /** The side holding the shield: two of the last three periods. `null` until
   *  somebody has actually won two, which is the honest state early on. */
  holder: string | null
  /** Who leads THIS period. Null when the two are level. */
  leader: 'a' | 'b' | null
  /**
   * The gap, phrased from the trailing side's point of view and always
   * positive: "84K back", never "-84K". A minus sign in front of a group of
   * colleagues on an all-staff board is a verdict, not a measure.
   */
  gapText: string
  /**
   * True when BOTH sides clear the contest floor. When false the fixture is
   * still drawn — it is a real comparison — but the view says out loud that it
   * cannot move the shield, because a side that could not be fielded did not
   * lose.
   */
  countsForShield: boolean
  /** The groups the two sides share, when the fixture mixes dimensions.
   *  Empty for a same-dimension fixture, where the sides are disjoint by
   *  construction. */
  overlap: number
}

export interface RivalryPool {
  /** People in groups under the contest floor. */
  count: number
  /** How many distinct groups they span. */
  groups: number
  /** The pool's own per-person score, or null when the pool is itself under
   *  the floor. */
  score: number | null
  scoreText: string | null
}

export interface RivalryModel {
  kind: 'office' | 'team'
  /** EVERY group, best first. Nothing is suppressed: `thin` marks the ones
   *  under the contest floor and `fieldable` marks the ones that can play. */
  sides: RivalryStanding[]
  /**
   * The old pooled group, and it is null unless a viewer asks for it.
   *
   * Kept as an OPTION rather than deleted so the previous behaviour is still
   * inspectable side by side with the new one — which is how you see what the
   * pooling actually cost. It is never the default.
   */
  pool: RivalryPool | null
  /** People with no group set at all. Counted and shown so the number is
   *  honest, never folded into a side and never rendered as a loss. */
  unassigned: number
  /** The selected fixture, or null when fewer than two groups exist at all. */
  fixture: RivalryFixture | null
  /** How many active people a group needs to be FIELDED. Not an exposure
   *  control -- see `RivalryStanding`. */
  contestFloor: number
}

/** Canonical pair ordering, matching the server's `side_a < side_b`. Two
 *  orderings of one pairing must resolve to one stored series or the shield
 *  flickers between them. */
function canonicalPair(x: string, y: string): [string, string] {
  return x < y ? [x, y] : [y, x]
}

/** Tokens per active person for a set of players. Rounded, because a
 *  scoreline with decimals invites a precision the measure does not have. */
function perPerson(tokens: number, active: number): number {
  return active === 0 ? 0 : Math.round(tokens / active)
}

/**
 * The provisional series, derived from data already in the payload.
 *
 * Every player carries 21 daily token buckets (the row heartbeat). Split into
 * three seven-day blocks that is a best-of-three with no new endpoint, no new
 * field, and no stored state — which is what lets the rivalry views render
 * something honest before a single week has been closed.
 *
 * A block in which either side fielded nobody is a `null` pip, not a win: a
 * side that did not turn up did not lose.
 */
function provisionalPips(a: BoardPlayer[], b: BoardPlayer[]): Pip[] {
  const BLOCKS = 3
  const pips: Pip[] = []
  const span = Math.max(...a.concat(b).map((p) => p.spark.length), 0)
  // Fewer days than blocks and two of the three blocks are empty by
  // construction, which reads as "neither side turned up" rather than as "the
  // window is too short to split". Same rule as the bump: a form the window
  // cannot support returns nothing and the view says why.
  if (span < BLOCKS) return []
  const width = Math.max(1, Math.floor(span / BLOCKS))
  for (let i = 0; i < BLOCKS; i++) {
    const from = i * width
    const to = i === BLOCKS - 1 ? span : (i + 1) * width
    const sum = (side: BoardPlayer[]) => {
      let tokens = 0
      let active = 0
      for (const p of side) {
        const t = p.spark.slice(from, to).reduce((n, v) => n + v, 0)
        if (t > 0) {
          tokens += t
          active += 1
        }
      }
      return { tokens, active }
    }
    const sa = sum(a)
    const sb = sum(b)
    if (sa.active === 0 || sb.active === 0) {
      pips.push(null)
      continue
    }
    const pa = perPerson(sa.tokens, sa.active)
    const pb = perPerson(sb.tokens, sb.active)
    pips.push(pa === pb ? null : pa > pb ? 'a' : 'b')
  }
  return pips
}

/** Two of the last three periods takes the shield. Fewer than two wins by
 *  anybody means nobody holds it yet, which is said out loud rather than
 *  defaulted to whoever happens to lead today. */
function shieldHolder(pips: Pip[], aName: string, bName: string): string | null {
  const wins = { a: 0, b: 0 }
  for (const p of pips) if (p) wins[p] += 1
  if (wins.a >= 2) return aName
  if (wins.b >= 2) return bName
  return null
}

/** Groups the board by one dimension, dropping nobody. `Unassigned` is
 *  counted separately rather than competing as a group, because "we do not
 *  know where you sit" is not a team. */
function groupBy(players: BoardPlayer[], kind: 'office' | 'team'): {
  groups: Map<string, BoardPlayer[]>
  unassigned: number
} {
  const groups = new Map<string, BoardPlayer[]>()
  let unassigned = 0
  for (const p of players) {
    const g = (kind === 'office' ? p.office : p.department) || 'Unassigned'
    if (g === 'Unassigned') {
      unassigned += 1
      continue
    }
    const list = groups.get(g)
    if (list) list.push(p)
    else groups.set(g, [p])
  }
  return { groups, unassigned }
}

/** One group's standing. The scoring denominator is ACTIVE people, never
 *  headcount, so a bigger group cannot win on size. */
function standingFor(
  name: string,
  dimension: 'office' | 'team',
  list: BoardPlayer[],
  contestFloor: number,
): RivalryStanding {
  const active = list.filter((p) => p.tokens > 0).length
  const tokens = list.reduce((n, p) => n + p.tokens, 0)
  const score = perPerson(tokens, active)
  const fieldable = active >= contestFloor
  return {
    name,
    dimension,
    score,
    scoreText: fmtTokens(score),
    active,
    members: list.length,
    fieldable,
    thin: !fieldable,
  }
}

/**
 * Builds one rivalry view.
 *
 * Takes `BoardPlayer[]` rather than the raw payload deliberately: a
 * `BoardPlayer` has no `spend` on it at all, so these views cannot put a cost
 * figure on screen even by accident, and no hide-costs branch is needed
 * anywhere in this function.
 *
 * `select` names the two sides to draw. When it is absent, or names a side
 * that does not exist, the two strongest FIELDABLE sides are used, falling
 * back to the two strongest of any size — so the view always opens on a real
 * fixture, and a fixture that cannot move the shield says so rather than
 * refusing to appear.
 */
export function buildRivalry(
  players: BoardPlayer[],
  kind: 'office' | 'team',
  series: SeriesHistory[],
  contestFloor: number,
  select?: { a: string; b: string },
  options?: { poolBelowFloor?: boolean },
): RivalryModel {
  const { groups, unassigned } = groupBy(players, kind)

  const sides: RivalryStanding[] = [...groups.entries()]
    .map(([name, list]) => standingFor(name, kind, list, contestFloor))
    .sort((x, y) => y.score - x.score)

  // The pool is now an OPTION, not the default. It exists so the previous
  // behaviour stays inspectable beside the new one; nothing computes it
  // unless a viewer asks.
  let pool: RivalryPool | null = null
  if (options?.poolBelowFloor) {
    const below = sides.filter((s) => s.thin)
    const poolActive = below.reduce((n, s) => n + s.active, 0)
    const poolTokens = below.reduce((n, s) => n + s.score * s.active, 0)
    const poolScore = poolActive >= contestFloor ? perPerson(poolTokens, poolActive) : null
    pool = {
      count: poolActive,
      groups: below.length,
      score: poolScore,
      scoreText: poolScore === null ? null : fmtTokens(poolScore),
    }
  }

  let fixture: RivalryFixture | null = null
  if (sides.length >= 2) {
    const pick = (name: string | undefined) => (name ? sides.find((s) => s.name === name) : undefined)
    // The DEFAULT fixture prefers two sides that can actually move the shield;
    // an explicit selection is honoured whatever its size, because a viewer
    // asking to compare two small offices is asking a real question and the
    // view answers it with the shield caveat attached rather than refusing.
    //
    // The fallbacks go to the DEFAULTS, never to "the next group in the list".
    // Falling through to the list order is how a one-person group with a huge
    // per-person score ends up as the opening fixture on the whole company's
    // board, which is precisely what the contest floor exists to stop.
    const strong = sides.filter((s) => s.fieldable)
    const defaultA = strong[0] ?? sides[0]!
    const defaultB = strong.find((s) => s !== defaultA) ?? sides.find((s) => s !== defaultA)!
    const a = pick(select?.a) ?? defaultA
    const chosenB = pick(select?.b)
    const b = chosenB && chosenB !== a ? chosenB : defaultB !== a ? defaultB : sides.find((s) => s !== a)!

    const [canonA, canonB] = canonicalPair(a.name, b.name)
    const stored = series.find(
      (h) => h.groupingKind === kind && h.sideA === canonA && h.sideB === canonB,
    )

    let pips: Pip[]
    let isStored: boolean
    if (stored && stored.periods.length > 0) {
      // Stored periods arrive newest first; the strip reads oldest first.
      pips = [...stored.periods]
        .reverse()
        .map((period) =>
          period.winner === null ? null : period.winner === a.name ? 'a' : period.winner === b.name ? 'b' : null,
        )
      isStored = true
    } else {
      pips = provisionalPips(groups.get(a.name) ?? [], groups.get(b.name) ?? [])
      isStored = false
    }

    const gap = Math.abs(a.score - b.score)
    fixture = {
      a: { name: a.name, score: a.score, scoreText: a.scoreText, active: a.active },
      b: { name: b.name, score: b.score, scoreText: b.scoreText, active: b.active },
      pips,
      stored: isStored,
      holder: shieldHolder(pips, a.name, b.name),
      leader: a.score === b.score ? null : a.score > b.score ? 'a' : 'b',
      // Always "N back": the number describes the distance, and the sentence
      // is built around the trailing side rather than pointed at it.
      gapText: gap === 0 ? 'level' : `${fmtTokens(gap)} back`,
      countsForShield: a.fieldable && b.fieldable,
      // Same dimension, so the two sides are disjoint by construction.
      overlap: 0,
    }
  }

  return { kind, sides, pool, unassigned, fixture, contestFloor }
}

/* ==========================================================================
 * THE LEAGUE: a duel becomes a table
 *
 * WHY THE DUEL HAD TO GO. Under the old five-person floor exactly ONE office
 * cleared it (Sydney) and exactly TWO teams did (IT, Actuarial & Data). Every
 * visit, in either grouping, produced the same fixture forever — the fixture
 * picker did not even render in the office grouping, because there was only
 * one legal pairing. That is why the view did not land, and nothing about the
 * visual treatment was the problem.
 *
 * A ranked table of every selected group works at two groups and at fourteen,
 * and it survives the platform growing, which a two-side arena does not. The
 * head-to-head arena is kept for the case it is genuinely good at — exactly
 * two groups selected — with all five of the rules that made it safe.
 * ======================================================================== */

/** The selector's state: any number of offices and teams, at once. */
export interface LeagueSelection {
  offices: string[]
  teams: string[]
}

/** Which dimension the league opens on. `both` ranks offices and teams in one
 *  table, which is legal and is why the overlap is stated. */
export type LeagueDimension = 'office' | 'team' | 'both'

/**
 * THE DEFAULT SELECTION, AND WHY IT IS A FUNCTION RATHER THAN A LITERAL.
 *
 * Origin's league tab rendered NOTHING on the live board while the selector
 * beside it rendered its chips correctly, and the cause was that the default
 * selection is DERIVED state with an empty value in its range. Two inputs
 * produce an empty table with a populated selector:
 *
 *  1. The view's dimension control is hard-coded to open on `office`, so a
 *     board whose office dimension resolves to no groups — every person
 *     `Unassigned`, which is what a stalled `user_directory` sync produces —
 *     selects nothing at all while the TEAM chips underneath it are fine.
 *  2. The chips open ALL-ON, so clicking one REMOVES it. A viewer who reads
 *     them as "pick the groups you want" and clicks four of them has emptied
 *     the selection, and the league tab is blank with no way back except the
 *     dimension control, which does not look like a reset.
 *
 * Both are the same defect: an empty selection was a reachable state and the
 * league had nothing to say in it. So the empty selection is now a synonym for
 * the default rather than a state of its own, and the default FALLS BACK
 * ACROSS DIMENSIONS rather than insisting on the one it was asked for. A
 * league that can render nothing is a league that will.
 */
export function defaultLeagueSelection(
  allOffices: RivalryStanding[],
  allTeams: RivalryStanding[],
  dimension: LeagueDimension = 'office',
): LeagueSelection {
  const offices = allOffices.map((s) => s.name)
  const teams = allTeams.map((s) => s.name)
  if (dimension === 'both') return { offices, teams }
  const wanted = dimension === 'office' ? { offices, teams: [] } : { offices: [], teams }
  if (wanted.offices.length > 0 || wanted.teams.length > 0) return wanted
  // The asked-for dimension is empty. Fall back to the other one rather than
  // returning nothing: a board with teams and no offices still has a league.
  return dimension === 'office' ? { offices: [], teams } : { offices, teams: [] }
}

/** True when a selection names no group at all — the state the league treats
 *  as "show me the default" rather than as "show me nothing". */
export function isEmptySelection(selection: LeagueSelection): boolean {
  return selection.offices.length === 0 && selection.teams.length === 0
}

export interface LeagueModel {
  /** Every SELECTED group, best first, across both dimensions. */
  table: RivalryStanding[]
  /** Every selectable group, so the chips can print an `n` without a second
   *  pass over the board. */
  allOffices: RivalryStanding[]
  allTeams: RivalryStanding[]
  /**
   * Pairs of selected groups that share people, with the count.
   *
   * A MIXED selection is not disjoint: Sydney and IT share four people, so a
   * league that ranked both without saying so would invite a reader to add
   * the two columns. The view states the overlap rather than letting the
   * assumption stand.
   */
  overlaps: Array<{ a: string; b: string; shared: number }>
  /** People with no office set, and people with no team set. Counted and
   *  shown, never folded into a group they are not in. */
  unassignedOffice: number
  unassignedTeam: number
  contestFloor: number
  /** True when the caller asked for nothing and got the default instead. The
   *  view says so, because a table that silently ignores its own selector is
   *  the failure this replaced. */
  defaulted: boolean
}

/** A group's member set, for the overlap calculation. Ids, not names: the
 *  opaque id is what identifies a row, and it never leaves this function. */
function memberIds(players: BoardPlayer[], dimension: 'office' | 'team', name: string): Set<string> {
  const out = new Set<string>()
  for (const p of players) {
    if ((dimension === 'office' ? p.office : p.department) === name) out.add(p.id)
  }
  return out
}

/**
 * Builds the league table across both dimensions at once.
 *
 * `selection` names the groups to rank, and AN EMPTY SELECTION MEANS "every
 * group in `dimension`" — the contract this docstring has always claimed and
 * the code did not keep. It returned an empty table instead, and the whole
 * defence against a blank league lived in one `useMemo` in the view. That is
 * the Origin bug: the compensation was in the caller, so every state that
 * reached the builder with an empty selection drew nothing. It is honoured
 * here now, so no caller can reintroduce it.
 */
export function buildLeague(
  players: BoardPlayer[],
  selection: LeagueSelection,
  contestFloor: number,
  dimension: LeagueDimension = 'office',
): LeagueModel {
  const office = groupBy(players, 'office')
  const team = groupBy(players, 'team')

  const allOffices = [...office.groups.entries()]
    .map(([name, list]) => standingFor(name, 'office', list, contestFloor))
    .sort((x, y) => y.score - x.score)
  const allTeams = [...team.groups.entries()]
    .map(([name, list]) => standingFor(name, 'team', list, contestFloor))
    .sort((x, y) => y.score - x.score)

  const defaulted = isEmptySelection(selection)
  const wanted = defaulted ? defaultLeagueSelection(allOffices, allTeams, dimension) : selection

  const table = [
    ...allOffices.filter((s) => wanted.offices.includes(s.name)),
    ...allTeams.filter((s) => wanted.teams.includes(s.name)),
  ].sort((x, y) => y.score - x.score)

  // Overlap is only possible ACROSS dimensions: two offices are disjoint, and
  // so are two teams, because a person has one of each. So the only pairs
  // worth testing are office x team, which keeps this O(offices x teams) on a
  // board of at most a few dozen groups.
  const overlaps: Array<{ a: string; b: string; shared: number }> = []
  for (const o of table.filter((s) => s.dimension === 'office')) {
    const oIds = memberIds(players, 'office', o.name)
    for (const t of table.filter((s) => s.dimension === 'team')) {
      const tIds = memberIds(players, 'team', t.name)
      let shared = 0
      for (const id of oIds) if (tIds.has(id)) shared += 1
      if (shared > 0) overlaps.push({ a: o.name, b: t.name, shared })
    }
  }
  overlaps.sort((x, y) => y.shared - x.shared)

  return {
    table,
    allOffices,
    allTeams,
    overlaps,
    unassignedOffice: office.unassigned,
    unassignedTeam: team.unassigned,
    contestFloor,
    defaulted,
  }
}

/* ==========================================================================
 * HEAD TO HEAD: a fixture the tab picks for itself
 *
 * WHY THIS EXISTS. Until 31 Aug 2026 the head-to-head tab drew a fixture only
 * when the LEAGUE selection happened to name exactly two groups, and rendered
 * a sentence telling the viewer to go to another tab and change something
 * otherwise. With the default selection — every office — that is four groups,
 * so the tab was a permanent instruction to leave it. A view whose only
 * content is a pointer to a different view is not a view.
 *
 * The two sides are now chosen ON the tab, one picker each, across both
 * dimensions, so "Sydney versus Underwriting" is a fixture a viewer can ask
 * for directly. The league keeps the multi-group selector; the arena stops
 * borrowing it.
 * ======================================================================== */

/** One side of a fixture, named by dimension so a mixed pairing is
 *  unambiguous: `Claims` is a team and `Sydney` is an office, and nothing
 *  stops a tenant from having both spellings in one namespace. */
export interface FixtureSide {
  dimension: 'office' | 'team'
  name: string
}

/** Value-equality for a side. Two pickers holding the same group must be
 *  detectable however the strings were constructed. */
export function sameSide(x: FixtureSide, y: FixtureSide): boolean {
  return x.dimension === y.dimension && x.name === y.name
}

/**
 * The opening fixture, and it is never null when two groups exist anywhere.
 *
 * Prefers two FIELDABLE sides from ONE dimension, because a same-dimension
 * fixture is disjoint by construction and needs no overlap caveat. Falls back
 * to a mixed fixture, then to the two strongest groups of any size — with the
 * shield caveat attached rather than by refusing to draw, which is the rule
 * `buildRivalry` already follows.
 */
export function defaultFixture(
  allOffices: RivalryStanding[],
  allTeams: RivalryStanding[],
): { a: FixtureSide; b: FixtureSide } | null {
  const side = (s: RivalryStanding): FixtureSide => ({ dimension: s.dimension, name: s.name })
  for (const pool of [allOffices, allTeams]) {
    const strong = pool.filter((s) => s.fieldable)
    if (strong.length >= 2) return { a: side(strong[0]!), b: side(strong[1]!) }
  }
  // No single dimension can field two. Cross the dimensions before giving up
  // on the floor -- "Sydney v IT" is a real question and it is the whole
  // reason the pickers are not restricted to one namespace.
  const strongOffice = allOffices.find((s) => s.fieldable)
  const strongTeam = allTeams.find((s) => s.fieldable)
  if (strongOffice && strongTeam) return { a: side(strongOffice), b: side(strongTeam) }
  const any = [...allOffices, ...allTeams].sort((x, y) => y.score - x.score)
  if (any.length >= 2) return { a: side(any[0]!), b: side(any[1]!) }
  return null
}

/**
 * One fixture between any two groups, in either dimension.
 *
 * Keeps all five rules that made the arena safe: no individual appears, the
 * score is per active person, both sides keep full saturation, the gap is
 * phrased from the trailing side and is always positive, and a fixture that
 * cannot move the shield says so instead of hiding.
 *
 * MIXED FIXTURES DECLARE THEIR OVERLAP. Two offices are disjoint and so are
 * two teams, but Sydney and IT share people, and a scoreline that let a reader
 * assume otherwise would be inviting them to add two columns that overlap.
 * `overlap` is the shared headcount and the view prints it.
 */
export function buildFixture(
  players: BoardPlayer[],
  a: FixtureSide,
  b: FixtureSide,
  series: SeriesHistory[],
  contestFloor: number,
): RivalryFixture | null {
  if (sameSide(a, b)) return null
  const membersOf = (s: FixtureSide) =>
    players.filter((p) => (s.dimension === 'office' ? p.office : p.department) === s.name)
  const listA = membersOf(a)
  const listB = membersOf(b)
  if (listA.length === 0 || listB.length === 0) return null

  const standA = standingFor(a.name, a.dimension, listA, contestFloor)
  const standB = standingFor(b.name, b.dimension, listB, contestFloor)

  // A stored series only exists for a SAME-DIMENSION pairing: the table is
  // keyed on one `grouping_kind`, and there has never been a mixed one to
  // store. A mixed fixture is therefore always provisional, and says so.
  let pips: Pip[]
  let isStored = false
  const stored =
    a.dimension === b.dimension
      ? (() => {
          const [canonA, canonB] = canonicalPair(a.name, b.name)
          return series.find(
            (h) => h.groupingKind === a.dimension && h.sideA === canonA && h.sideB === canonB,
          )
        })()
      : undefined
  if (stored && stored.periods.length > 0) {
    pips = [...stored.periods]
      .reverse()
      .map((period) =>
        period.winner === null ? null : period.winner === a.name ? 'a' : period.winner === b.name ? 'b' : null,
      )
    isStored = true
  } else {
    pips = provisionalPips(listA, listB)
  }

  const idsB = new Set(listB.map((p) => p.id))
  const overlap = listA.reduce((n, p) => n + (idsB.has(p.id) ? 1 : 0), 0)
  const gap = Math.abs(standA.score - standB.score)

  return {
    a: { name: standA.name, score: standA.score, scoreText: standA.scoreText, active: standA.active },
    b: { name: standB.name, score: standB.score, scoreText: standB.scoreText, active: standB.active },
    pips,
    stored: isStored,
    holder: shieldHolder(pips, standA.name, standB.name),
    leader: standA.score === standB.score ? null : standA.score > standB.score ? 'a' : 'b',
    gapText: gap === 0 ? 'level' : `${fmtTokens(gap)} back`,
    countsForShield: standA.fieldable && standB.fieldable,
    overlap,
  }
}

/* ==========================================================================
 * THE WEEKLY BUMP: position by week
 *
 * What the three-pip strip was trying to say with three dots, said for every
 * selected group at once. A week a group could not field the floor is a GAP,
 * not a loss: a side that did not turn up did not lose, and the shield does
 * not move on an absence.
 * ======================================================================== */

/** One group's position over the weeks. `rank` is null for a week the group
 *  could not field the contest floor — drawn as a break in the line. */
export interface BumpSeries {
  name: string
  dimension: 'office' | 'team'
  points: Array<{ week: number; rank: number | null; score: number }>
}

/** How many days of heartbeat make a week. */
const WEEK_DAYS = 7
/** A bump chart needs at least two columns to say anything: with one, the
 *  lines cannot cross and the form has no job. Below this the view prints why
 *  rather than drawing a row of dots and calling it a season. */
const MIN_BUMP_WEEKS = 2

/**
 * Weekly positions for a set of groups, from the daily heartbeat already on
 * every player.
 *
 * No new endpoint and no new field: the 21 daily token buckets split into
 * three seven-day blocks are three real weeks. Oldest first, so the chart
 * reads left to right.
 */
export function buildBump(
  players: BoardPlayer[],
  groups: RivalryStanding[],
  contestFloor: number,
): BumpSeries[] {
  const span = Math.max(...players.map((p) => p.spark.length), 0)
  if (span === 0 || groups.length === 0) return []
  // NOT `Math.max(1, ...)`. That floor is how a 24H window — one day of
  // heartbeat — produced a chart headed "Week 1" built from a single day, on a
  // card whose whole subject is position CHANGING between weeks. A window too
  // short for the form returns nothing and the view says why.
  const weeks = Math.floor(span / WEEK_DAYS)
  if (weeks < MIN_BUMP_WEEKS) return []

  const series: BumpSeries[] = groups.map((g) => ({ name: g.name, dimension: g.dimension, points: [] }))

  for (let w = 0; w < weeks; w++) {
    const from = span - (weeks - w) * WEEK_DAYS
    const to = from + WEEK_DAYS
    // Score every group for this week, then rank only those that fielded the
    // floor. A group under the floor gets a null rank, which the chart draws
    // as a gap rather than as last place.
    const weekly = groups.map((g) => {
      let tokens = 0
      let active = 0
      for (const p of players) {
        if ((g.dimension === 'office' ? p.office : p.department) !== g.name) continue
        const t = p.spark.slice(Math.max(0, from), to).reduce((n, v) => n + v, 0)
        if (t > 0) {
          tokens += t
          active += 1
        }
      }
      return { name: g.name, score: perPerson(tokens, active), active }
    })
    const fielded = weekly
      .filter((x) => x.active >= contestFloor)
      .sort((x, y) => y.score - x.score)
    const rankOf = new Map(fielded.map((x, i) => [x.name, i + 1]))
    for (const s of series) {
      const own = weekly.find((x) => x.name === s.name)!
      s.points.push({ week: w, rank: rankOf.get(s.name) ?? null, score: own.score })
    }
  }
  return series
}

/* ==========================================================================
 * THE RACE TRACK: departments as a field of runners, scrubbed over time
 *
 * WHY TOKENS AND NOT DOLLARS, and it is a payload fact rather than a
 * preference. Team Wars ranks departments on SPEND when costs are visible,
 * but there is no daily spend series anywhere on the wire — every player
 * carries daily TOKEN buckets and nothing else per day, and spend cannot be
 * derived from tokens because the price differs per model. A track whose
 * position moved with a scrubbed date therefore has exactly one measure it
 * can draw honestly, and the panel names it above the track rather than
 * letting the dollar figure on the card beside it be read as the same
 * quantity.
 * ======================================================================== */

/** One department on the track. `values[i]` is the running token total to day
 *  `i`, so a runner can only move forward; what changes is the order and the
 *  spacing. */
export interface TrackRunner {
  name: string
  dimension: 'office' | 'team'
  values: number[]
  /** Everyone in the group, and how many of them moved in this window. Both
   *  printed, because a lane with no `n` invites a headcount reading. */
  members: number
  active: number
}

/**
 * The field, ordered by where each runner finishes.
 *
 * Ordered on the FINAL total rather than on the scrubbed position, so the
 * lanes do not reshuffle under the viewer as the slider moves. A lane that
 * swaps rows mid-scrub destroys the one thing a race is good at — watching
 * somebody come through the field — because the eye loses the runner it was
 * following. Overtakes are read off the horizontal positions crossing, which
 * is where they actually happen.
 */
export function buildTrack(players: BoardPlayer[], kind: 'office' | 'team'): TrackRunner[] {
  const span = Math.max(...players.map((p) => p.spark.length), 0)
  if (span === 0) return []
  const { groups } = groupBy(players, kind)
  const runners: TrackRunner[] = [...groups.entries()].map(([name, list]) => {
    const daily = Array.from({ length: span }, (_, i) => list.reduce((n, p) => n + (p.spark[i] ?? 0), 0))
    return {
      name,
      dimension: kind,
      values: cumulative(daily),
      members: list.length,
      active: list.filter((p) => p.tokens > 0).length,
    }
  })
  return runners.sort((a, b) => (b.values[span - 1] ?? 0) - (a.values[span - 1] ?? 0))
}

/* ==========================================================================
 * THE HORSE RACE: cumulative totals, and where the lead actually changed
 * ======================================================================== */

/** One runner's cumulative series. `values[i]` is the running total to day i,
 *  so the line can only go up; what moves is the ORDER. */
export interface RaceSeries {
  name: string
  /** The player's opaque id, or null for the pooled tail. Never an email. */
  id: string | null
  values: number[]
}

/** A day the lead changed hands, computed FROM THE SERIES rather than
 *  annotated by hand — so the chart cannot claim an overtake that the numbers
 *  do not contain, and cannot miss one they do. */
export interface LeadChange {
  /** Index into `values`. */
  at: number
  from: string
  to: string
}

/**
 * Turns daily values into cumulative running totals.
 *
 * Separate from the chart so the lead-change computation reads the same
 * numbers the line is drawn from. Two implementations of "cumulative" is
 * exactly how a marker ends up on the wrong day.
 */
export function cumulative(daily: number[]): number[] {
  const out: number[] = []
  let running = 0
  for (const v of daily) {
    running += v
    out.push(running)
  }
  return out
}

/**
 * Every point at which the leader changed, read off the cumulative series.
 *
 * WHY COMPUTED AND NOT ANNOTATED. A cumulative chart where nobody overtakes is
 * a stacked ranking with extra steps, so the overtakes are the whole reason
 * the form earns its place — which makes a hand-placed marker the one thing
 * on the chart that must not be hand-placed. This walks the series and emits
 * the changes that are actually in it.
 *
 * A day where nobody has anything yet has no leader and produces no change: a
 * race has not started until somebody has run.
 */
export function computeLeadChanges(series: RaceSeries[]): LeadChange[] {
  if (series.length < 2) return []
  const length = Math.max(...series.map((s) => s.values.length), 0)
  const changes: LeadChange[] = []
  let leader: string | null = null
  for (let i = 0; i < length; i++) {
    let best: { name: string; value: number } | null = null
    let tied = false
    for (const s of series) {
      const v = s.values[i] ?? 0
      if (v <= 0) continue
      if (best === null || v > best.value) {
        best = { name: s.name, value: v }
        tied = false
      } else if (v === best.value) {
        tied = true
      }
    }
    // A tie at the front is not a change of leader. Emitting one would put a
    // marker on a day where the order did not actually move.
    if (best === null || tied) continue
    if (leader !== null && best.name !== leader) {
      changes.push({ at: i, from: leader, to: best.name })
    }
    leader = best.name
  }
  return changes
}

/**
 * The race, built from the heartbeat every player already carries.
 *
 * `named` runners get their own line; the rest are pooled into ONE series so
 * the chart never generates a seventh hue it cannot keep distinct under
 * colour-vision deficiency. THE POOLING IS A CHARTING DECISION, NOT A PRIVACY
 * ONE, and the view says so: every pooled person is named in the table below
 * the chart. Conflating the two is what produced the floor confusion in the
 * first place.
 */
export function buildRace(players: BoardPlayer[], named = 5): { series: RaceSeries[]; leadChanges: LeadChange[] } {
  const span = Math.max(...players.map((p) => p.spark.length), 0)
  if (span === 0 || players.length === 0) return { series: [], leadChanges: [] }

  const ordered = [...players].sort((a, b) => b.tokens - a.tokens)
  const head = ordered.slice(0, named)
  const tail = ordered.slice(named)

  const series: RaceSeries[] = head.map((p) => ({
    name: p.first,
    id: p.id,
    values: cumulative(Array.from({ length: span }, (_, i) => p.spark[i] ?? 0)),
  }))
  if (tail.length > 0) {
    const pooled = Array.from({ length: span }, (_, i) =>
      tail.reduce((n, p) => n + (p.spark[i] ?? 0), 0),
    )
    series.push({ name: `${tail.length} more`, id: null, values: cumulative(pooled) })
  }
  return { series, leadChanges: computeLeadChanges(series) }
}
